import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GiftIcon, PresentationChartLineIcon, UserCircleIcon, Cog6ToothIcon, ArrowRightIcon, BuildingOfficeIcon, UsersIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';

const DashboardCard = ({ to, icon, title, children, color }) => (
  <Link to={to} className={`block p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${color}`}>
    <div className="flex items-center">
      <div className="p-3 rounded-full bg-white/20">
        {icon}
      </div>
      <h3 className="ml-4 text-xl font-bold text-white">{title}</h3>
      <ArrowRightIcon className="w-6 h-6 ml-auto text-white/50" />
    </div>
    <p className="mt-2 text-white/80">{children}</p>
  </Link>
);

export default function OrganizationDashboardPage() {
  const { user } = useAuth();
  const [hasRequested, setHasRequested] = useState(false);
  
  useEffect(() => {
    // This logic can be improved later by fetching the organization's active campaign from the backend
    const requestStatus = localStorage.getItem('orgRequestSubmitted') === 'true';
    setHasRequested(requestStatus);
  }, []);
  
  const orgName = user?.organizationName || user?.first_name || 'Organization';

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Welcome, {orgName}!</h1>
      <p className="mt-1 text-gray-500 dark:text-gray-400">This is your organization dashboard. Manage your campaigns and organization profile from here.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {hasRequested ? (
          <DashboardCard
            to="/organization/status"
            icon={<PresentationChartLineIcon className="w-8 h-8 text-white" />}
            title="Campaign Status"
            color="bg-gradient-to-br from-green-500 to-green-700"
          >
            Your organization's request is pending. Click here to view the current status of your campaign.
          </DashboardCard>
        ) : (
          <DashboardCard
            to="/organization/catalog"
            icon={<GiftIcon className="w-8 h-8 text-white" />}
            title="Start a Campaign"
            color="bg-gradient-to-br from-orange-500 to-orange-700"
          >
            Your organization hasn't requested any items yet. Browse the catalog to start a campaign.
          </DashboardCard>
        )}

        <DashboardCard
          to="/organization/profile"
          icon={<BuildingOfficeIcon className="w-8 h-8 text-white" />}
          title="Organization Profile"
          color="bg-gradient-to-br from-blue-500 to-blue-700"
        >
          View and manage your organization's information, registration documents, and profile.
        </DashboardCard>

        <DashboardCard
          to="/organization/team"
          icon={<UsersIcon className="w-8 h-8 text-white" />}
          title="Team Members"
          color="bg-gradient-to-br from-purple-500 to-purple-700"
        >
          Manage team members who can access and manage your organization's campaigns.
        </DashboardCard>
        
        <DashboardCard
          to="/organization/settings"
          icon={<Cog6ToothIcon className="w-8 h-8 text-white" />}
          title="Settings"
          color="bg-gradient-to-br from-slate-600 to-slate-800"
        >
          Manage your organization account settings, team permissions, and preferences.
        </DashboardCard>
      </div>

      {/* Organization Stats Section */}
      <div className="mt-12 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Organization Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">0</div>
            <div className="text-sm text-blue-800 dark:text-blue-200 mt-1">Active Campaigns</div>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">0</div>
            <div className="text-sm text-green-800 dark:text-green-200 mt-1">Devices Received</div>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">0</div>
            <div className="text-sm text-purple-800 dark:text-purple-200 mt-1">Team Members</div>
          </div>
        </div>
      </div>
    </div>
  );
}